import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';

import { ArchiveIcon, DialogueIcon, LibraryIcon } from '@treams/treams-icons';
import { Subheading, Sidebar, SidebarLayout, Skeleton, Empty } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import CreateFeedbackTemplate from './CreateFeedbackTemplate';

import FeedbackLibrary from './FeedbackLibrary';
import { Route, Switch } from '@treams/treams-routes';
import FeedbackTemplate from './FeedbackTemplate';

export default function FeedbackSettings() {
	const { t } = useTranslation();

	const query = graphql.useFeedbackTemplatesQuery();
	const { localize } = useLocalize();

	const [optimistic, setOptimistic] = useState<graphql.FeedbackTemplate[]>([]);

	const [archived, templates] = _.partition(
		_.sortBy(
			_.uniqBy(
				query.data?.feedbackTemplates.edges.map(({ node }) => node).concat(optimistic),
				'id',
			),
			[(template) => template.isArchived, (template) => localize(template.title).text],
		),
		(template) => template.isArchived,
	);

	return (
		<SidebarLayout
			sidebar={
				<Sidebar>
					<Sidebar.Item
						small
						icon={<LibraryIcon />}
						title={t('settings.feedbackLibrary.title')}
						exact
						path="/settings/feedback-v2"
					/>
					<Sidebar.Item
						small
						icon={<ArchiveIcon />}
						title={t('settings.feedbackLibrary.archive.title')}
						exact
						path="/settings/feedback-v2/archive"
					/>
					<Sidebar.Separator />
					<Subheading mb={2}> {t('settings.feedbackLibrary.templates.title')}</Subheading>
					{query.data ? (
						<>
							{templates.map((template) => (
								<Sidebar.Item
									key={template.id}
									title={localize(template.title).text}
									small
									exact
									path={`/settings/feedback-v2/${template.id}`}
								/>
							))}
							{templates.length === 0 && (
								<Skeleton.Empty
									contents={
										<Empty
											icon={<DialogueIcon />}
											title={t('settings.feedbackLibrary.templates.empty.title')}
											description={t('settings.feedbackLibrary.templates.empty.description')}
											small
										/>
									}
								>
									<Skeleton.Repeat count={3}>
										<Sidebar.Item.Skeleton small />
									</Skeleton.Repeat>
								</Skeleton.Empty>
							)}
							<Sidebar.Spacing />
							<Flex>
								<CreateFeedbackTemplate
									onCreate={(template) =>
										setOptimistic((optimistic) => optimistic.concat([template]))
									}
								/>
							</Flex>
							{archived.length > 0 && (
								<>
									<Sidebar.Separator />
									<Subheading mb={2}>{t('settings.feedbackLibrary.templates.archived')}</Subheading>
									{archived.map((template) => (
										<Sidebar.Item
											key={template.id}
											title={localize(template.title).text}
											small
											exact
											path={`/settings/feedback-v2/${template.id}`}
										/>
									))}
								</>
							)}
						</>
					) : (
						<Skeleton.Repeat count={3}>
							<Sidebar.Item.Skeleton small />
						</Skeleton.Repeat>
					)}
				</Sidebar>
			}
		>
			<Switch>
				<Route path="/settings/feedback-v2/archive">
					<FeedbackLibrary archived />
				</Route>
				<Route<{ templateID: string }> path="/settings/feedback-v2/:templateID">
					{({ match }) => <FeedbackTemplate templateID={match!.params.templateID!} />}
				</Route>
				<Route>
					<FeedbackLibrary />
				</Route>
			</Switch>
		</SidebarLayout>
	);
}
